/* global HTMLDivElement, HTMLElement, MouseEvent, Node, requestAnimationFrame */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, InputHTMLAttributes, KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Clock, X } from "lucide-react";

const TIME_MINUTE_STEP = 5;

function parseTimeValue(value: string | undefined): { h: number; m: number } | null {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) return null;
  const [h, m] = value.split(":").map(Number);
  if (h > 23 || m > 59) return null;
  return { h, m };
}

/**
 * Flexible time string parser — accepts many formats users may type.
 * Returns { h, m } with h ∈ [0,23], m ∈ [0,59], or null if invalid.
 *
 * Accepted formats:
 *   "HH:MM" / "H:MM"    → standard colon  e.g. "09:30", "9:30"
 *   "HH.MM" / "H.MM"    → dot separator   e.g. "9.30"
 *   "HhMM"  / "HhM"     → 'h' separator   e.g. "9h30", "9h5"
 *   "HgMM"  / "HgM"     → 'g' separator   e.g. "9g30" (Vietnamese "giờ")
 *   "HHMM"  (4 digits)  → compact         e.g. "0930", "930" (interpreted as "09:30")
 *   "H"     (1-2 digits) → hour only      e.g. "9" → 09:00, "14" → 14:00
 */
export function parseTypedTime(raw: string): { h: number; m: number } | null {
  const s = raw.trim();
  if (!s) return null;

  let hRaw: number | undefined;
  let mRaw: number | undefined;

  // Colon / dot separator: "9:30", "9.30"
  const sepMatch = s.match(/^(\d{1,2})[:.](\d{1,2})$/);
  if (sepMatch) {
    hRaw = parseInt(sepMatch[1], 10);
    mRaw = parseInt(sepMatch[2], 10);
  }

  // 'h' or 'g' separator (Vietnamese): "9h30", "9g30", "9h5", "9g"
  if (hRaw === undefined) {
    const hgMatch = s.match(/^(\d{1,2})[hgHG](\d{0,2})$/);
    if (hgMatch) {
      hRaw = parseInt(hgMatch[1], 10);
      mRaw = hgMatch[2] ? parseInt(hgMatch[2], 10) : 0;
    }
  }

  // Pure digits: "9" → 09:00, "14" → 14:00, "930" → 09:30, "0930" → 09:30
  if (hRaw === undefined && /^\d{1,4}$/.test(s)) {
    if (s.length <= 2) {
      hRaw = parseInt(s, 10);
      mRaw = 0;
    } else {
      // Last 2 digits = minutes, remainder = hours
      mRaw = parseInt(s.slice(-2), 10);
      hRaw = parseInt(s.slice(0, -2), 10);
    }
  }

  if (hRaw === undefined || mRaw === undefined) return null;

  // Reject out-of-range values (don't silently clamp "99:00" → "23:00")
  if (hRaw > 23 || mRaw > 59) return null;

  return { h: hRaw, m: mRaw };
}

/** Cuộn 1 ô vào giữa cột của nó (không dùng scrollIntoView để khỏi cuộn cả trang). */
function centerInColumn(column: HTMLDivElement | null, selector: string) {
  const el = column?.querySelector<HTMLElement>(selector);
  if (!column || !el) return;
  column.scrollTop = el.offsetTop - column.clientHeight / 2 + el.clientHeight / 2;
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function formatTimeDisplay(value: string): string {
  const parsed = parseTimeValue(value);
  if (!parsed) return "";
  return `${pad2(parsed.h)}:${pad2(parsed.m)}`;
}

/**
 * Shared bento custom time picker (24h, "HH:MM") — native time input giữ ẩn
 * cho form contract, popup 2 cột Giờ/Phút cùng ngôn ngữ với BentoDateInput.
 * Người dùng có thể gõ trực tiếp hoặc mở popup bằng nút đồng hồ.
 */
export function BentoTimeInput({ wrapClassName = "", className = "", ...props }: InputHTMLAttributes<HTMLInputElement> & { wrapClassName?: string }) {
  const { id, value, defaultValue, onChange, disabled, name, required, placeholder, ...inputProps } = props;
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() => String(defaultValue ?? ""));
  const currentValue = String(isControlled ? value ?? "" : internalValue);
  const selected = parseTimeValue(currentValue);

  // Draft: what the user is currently typing in the visible text field
  const [draft, setDraft] = useState(() => formatTimeDisplay(currentValue));
  const [focused, setFocused] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  // textRef is the visible editable input (replaces the old button trigger)
  const textRef = useRef<HTMLInputElement>(null);
  const iconBtnRef = useRef<HTMLButtonElement>(null);
  const nativeRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hourColRef = useRef<HTMLDivElement>(null);
  const minuteColRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const panelId = `${id ?? generatedId}-time`;
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ left: 0, top: 0, width: 224 });

  // Sync draft when external value changes and field is NOT focused
  useEffect(() => {
    if (!focused) {
      setDraft(formatTimeDisplay(currentValue));
    }
    // Only react to currentValue changes; `focused` used as gate, not dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  // Phút bước 5; nếu giá trị hiện tại lẻ thì chèn thêm để vẫn highlight được.
  const minuteOptions = useMemo(() => {
    const base = Array.from({ length: 60 / TIME_MINUTE_STEP }, (_, i) => i * TIME_MINUTE_STEP);
    if (selected && !base.includes(selected.m)) base.push(selected.m);
    return base.sort((a, b) => a - b);
  }, [selected]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !textRef.current) return undefined;
    const positionPanel = () => {
      const rect = textRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gap = 8;
      const panelWidth = Math.min(Math.max(rect.width, 224), Math.max(200, window.innerWidth - 16));
      const panelHeight = 296;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openUp = spaceBelow < panelHeight && spaceAbove > spaceBelow;
      setPanelPosition({
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - panelWidth - 8)),
        top: openUp ? Math.max(8, rect.top - panelHeight - gap) : Math.min(rect.bottom + gap, window.innerHeight - panelHeight - 8),
        width: panelWidth,
      });
    };
    positionPanel();
    window.addEventListener("resize", positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    return () => {
      window.removeEventListener("resize", positionPanel);
      window.removeEventListener("scroll", positionPanel, true);
    };
  }, [open]);

  // Mở popup → cuộn giờ/phút đang chọn vào giữa cột.
  useEffect(() => {
    if (!open) return;
    const target = selected ?? { h: 9, m: 0 };
    requestAnimationFrame(() => {
      centerInColumn(hourColRef.current, `[data-hour="${target.h}"]`);
      centerInColumn(minuteColRef.current, `[data-minute="${target.m}"]`);
    });
    // Chỉ chạy lúc mở
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function emitChange(nextValue: string) {
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    if (nativeRef.current) {
      nativeRef.current.value = nextValue;
      onChange?.({
        target: nativeRef.current,
        currentTarget: nativeRef.current,
      } as ChangeEvent<HTMLInputElement>);
    }
  }

  function commitDraft(raw: string) {
    if (!raw.trim()) {
      // Empty → emit "" (clear)
      emitChange("");
      setDraft("");
      return;
    }
    const parsed = parseTypedTime(raw);
    if (parsed) {
      const normalized = `${pad2(parsed.h)}:${pad2(parsed.m)}`;
      emitChange(normalized);
      setDraft(`${pad2(parsed.h)}:${pad2(parsed.m)}`);
    } else {
      // Invalid → revert to last valid display
      setDraft(formatTimeDisplay(currentValue));
    }
  }

  function setPart(part: "h" | "m", val: number) {
    const base = selected ?? { h: 9, m: 0 };
    const next = part === "h" ? { h: val, m: base.m } : { h: base.h, m: val };
    const normalized = `${pad2(next.h)}:${pad2(next.m)}`;
    emitChange(normalized);
    setDraft(`${pad2(next.h)}:${pad2(next.m)}`);
    if (part === "m") {
      setOpen(false);
      textRef.current?.focus();
    }
  }

  function chooseNow() {
    const now = new Date();
    const normalized = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
    emitChange(normalized);
    setDraft(normalized);
    setOpen(false);
    textRef.current?.focus();
  }

  function handleTextKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commitDraft(draft);
      setOpen(false);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setDraft(formatTimeDisplay(currentValue));
      setOpen(false);
      return;
    }
  }

  function handleNativeChange(event: ChangeEvent<HTMLInputElement>) {
    if (!isControlled) {
      setInternalValue(event.target.value);
    }
    onChange?.(event);
  }

  const timeColumn = (label: string, colRef: typeof hourColRef, options: number[], part: "h" | "m", selectedVal: number | undefined) => (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className="text-center text-[11px] font-bold uppercase tracking-[0.08em] text-ink-400">{label}</span>
      <div ref={colRef} className="max-h-44 overflow-y-auto rounded-xl border border-ink-100 bg-ink-50/50 p-1">
        {options.map((val) => (
          <button
            key={val}
            type="button"
            data-hour={part === "h" ? val : undefined}
            data-minute={part === "m" ? val : undefined}
            aria-pressed={selectedVal === val}
            onClick={() => setPart(part, val)}
            className={`block w-full rounded-lg py-1.5 text-center text-[13px] font-bold transition ${
              selectedVal === val ? "bg-brand-500 text-white shadow-sm" : "text-ink-700 hover:bg-ink-100"
            }`}
          >
            {pad2(val)}
          </button>
        ))}
      </div>
    </div>
  );

  const showClear = currentValue && !required && !disabled;

  return (
    <div ref={rootRef} className={`group relative ${wrapClassName}`}>
      {/* Hidden native input — form contract (callers receive onChange with target.value = "HH:MM") */}
      <input
        {...inputProps}
        ref={nativeRef}
        id={`${id ?? generatedId}-native`}
        name={name}
        required={required}
        disabled={disabled}
        type="time"
        value={currentValue}
        onChange={handleNativeChange}
        tabIndex={-1}
        className="sr-only"
      />

      {/* Visible row: text input + icon toggle */}
      <div
        className={`flex h-11 w-full items-center rounded-xl border border-ink-200 bg-white/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] transition duration-150 focus-within:border-brand-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-brand-100/70 hover:border-ink-300 hover:bg-white ${disabled ? "cursor-not-allowed opacity-50" : ""} ${className}`}
      >
        <input
          id={id}
          ref={textRef}
          type="text"
          inputMode="numeric"
          disabled={disabled}
          aria-label={placeholder || "Giờ (HH:MM)"}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          placeholder={placeholder || "--:--"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => {
            setFocused(true);
            // Select all text on focus for easy replacement
            textRef.current?.select();
          }}
          onBlur={() => {
            setFocused(false);
            commitDraft(draft);
          }}
          onKeyDown={handleTextKeyDown}
          className={`min-w-0 flex-1 bg-transparent px-3.5 py-2.5 text-[13px] font-semibold outline-none placeholder:text-ink-400 ${selected ? "text-ink-900" : "text-ink-400"} ${showClear ? "pr-0" : ""}`}
        />

        {/* Clear button */}
        {showClear ? (
          <button
            type="button"
            aria-label="Xoá giờ"
            tabIndex={-1}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              emitChange("");
              setDraft("");
              setOpen(false);
            }}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-ink-400 transition hover:bg-rose-50 hover:text-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}

        {/* Clock icon — popup toggle */}
        <button
          type="button"
          ref={iconBtnRef}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Mở chọn giờ"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((prev) => !prev);
            // Keep focus on text input so keyboard still works
            textRef.current?.focus();
          }}
          className="mr-1.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white text-ink-500 shadow-sm ring-1 ring-ink-200 transition hover:text-brand-600 group-focus-within:bg-brand-50 group-focus-within:text-brand-600 group-focus-within:ring-brand-100"
        >
          <Clock className="h-3.5 w-3.5" />
        </button>
      </div>

      {open && createPortal(
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label="Chọn giờ"
          style={{
            left: panelPosition.left,
            top: panelPosition.top,
            width: panelPosition.width,
          }}
          className="fixed z-[90] rounded-2xl border border-ink-200 bg-white p-3 shadow-[0_24px_70px_rgba(31,29,43,0.2)] ring-1 ring-white/70"
          onMouseDown={(event) => event.preventDefault()}
        >
          <div className="flex gap-2">
            {timeColumn("Giờ", hourColRef, Array.from({ length: 24 }, (_, h) => h), "h", selected?.h)}
            {timeColumn("Phút", minuteColRef, minuteOptions, "m", selected?.m)}
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-ink-100 pt-3">
            <button
              type="button"
              onClick={chooseNow}
              className="rounded-lg bg-brand-50 px-3 py-1.5 text-[12px] font-bold text-brand-600 transition hover:bg-brand-100"
            >
              Bây giờ
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                textRef.current?.focus();
              }}
              className="rounded-lg px-3 py-1.5 text-[12px] font-bold text-ink-500 transition hover:bg-ink-100 hover:text-ink-800"
            >
              Xong
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
